import * as React from 'react';

/**
 * Detects the Eitaa mini-app host and returns its signed `initData`.
 *
 * The messenger injects `window.Eitaa.WebApp` following the Telegram WebApp
 * protocol; `window.Telegram.WebApp` is accepted as well because some builds
 * expose only that alias. Absence of both simply means the app is running in a
 * normal browser and the OTP flow applies.
 */
export interface MiniAppContext {
  /** True when a signed payload is available to attempt mini-app login. */
  isMiniApp: boolean;
  initData: string | null;
}

function readMiniAppContext(): MiniAppContext {
  const webApp = window.Eitaa?.WebApp ?? window.Telegram?.WebApp;
  const initData = webApp?.initData?.trim();
  // The bridge exists but hands over an empty payload when the app is opened
  // outside a chat; without signed data there is nothing to authenticate.
  if (!webApp || !initData) return { isMiniApp: false, initData: null };
  return { isMiniApp: true, initData };
}

export function useMiniApp(): MiniAppContext {
  const [context] = React.useState(readMiniAppContext);

  React.useEffect(() => {
    if (!context.isMiniApp) return;
    const webApp = window.Eitaa?.WebApp ?? window.Telegram?.WebApp;
    // Tell the host we have painted, and use the full viewport height.
    webApp?.ready?.();
    webApp?.expand?.();
  }, [context.isMiniApp]);

  return context;
}
